import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import type { CategoryProduct } from "@/lib/catalog";

export interface CartItem {
  productId: number;
  title: string;
  price: number;
  currency: string | null;
  image: string | null;
  quantity: number;
}

interface CartContextValue {
  items: CartItem[];
  totalItems: number;
  totalPrice: number;
  addItem: (product: CategoryProduct) => void;
  removeItem: (productId: number) => void;
  updateQuantity: (productId: number, quantity: number) => void;
  clear: () => void;
  isInCart: (productId: number) => boolean;
}

const STORAGE_KEY = "vendeya_cart_v1";

const CartContext = createContext<CartContextValue | undefined>(undefined);

function readLocal(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as CartItem[]) : [];
  } catch {
    return [];
  }
}

function writeLocal(items: CartItem[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Si el almacenamiento no está disponible, se ignora.
  }
}

function mergeItems(...lists: CartItem[][]): CartItem[] {
  const map = new Map<number, CartItem>();
  lists.flat().forEach((item) => {
    const existing = map.get(item.productId);
    if (existing) {
      existing.quantity += item.quantity;
    } else {
      map.set(item.productId, { ...item });
    }
  });
  return Array.from(map.values());
}

async function loadRemote(userId: string): Promise<CartItem[]> {
  try {
    const { data, error } = await supabase
      .from("carts")
      .select("items")
      .eq("user_id", userId)
      .maybeSingle();
    if (error || !data?.items) return [];
    return Array.isArray(data.items) ? (data.items as CartItem[]) : [];
  } catch {
    return [];
  }
}

async function saveRemote(userId: string, items: CartItem[]): Promise<void> {
  try {
    await supabase.from("carts").upsert(
      { user_id: userId, items, updated_at: new Date().toISOString() },
      { onConflict: "user_id" },
    );
  } catch {
    // La copia local sigue siendo la fuente para la UI aunque falle el sync.
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [ready, setReady] = useState(false);
  const syncedUserId = useRef<string | null>(null);

  // 1. Carga inicial desde el navegador.
  useEffect(() => {
    setItems(readLocal());
    setReady(true);
  }, []);

  // 2. Persistencia local en cada cambio.
  useEffect(() => {
    if (!ready) return;
    writeLocal(items);
  }, [items, ready]);

  // 3. Al iniciar sesión: fusiona el carrito del Backend con el local.
  useEffect(() => {
    if (!ready) return;
    const userId = user?.id ?? null;

    if (!userId) {
      syncedUserId.current = null;
      return;
    }

    let cancelled = false;
    (async () => {
      const remote = await loadRemote(userId);
      if (cancelled) return;
      const local = readLocal();
      const merged = mergeItems(remote, local);
      setItems(merged);
      syncedUserId.current = userId;
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, ready]);

  // 4. Empuja al Backend en cada cambio cuando hay sesión.
  useEffect(() => {
    if (!ready) return;
    const userId = user?.id ?? null;
    if (!userId) return;
    if (syncedUserId.current !== userId) return;
    saveRemote(userId, items);
  }, [items, user?.id, ready]);

  const addItem = useCallback((product: CategoryProduct) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.productId === product.id);
      if (existing) {
        return prev.map((item) =>
          item.productId === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      return [
        ...prev,
        {
          productId: product.id,
          title: product.title,
          price: Number(product.price ?? 0),
          currency: product.currency ?? null,
          image: product.images?.[0] ?? null,
          quantity: 1,
        },
      ];
    });
  }, []);

  const removeItem = useCallback((productId: number) => {
    setItems((prev) => prev.filter((item) => item.productId !== productId));
  }, []);

  const updateQuantity = useCallback((productId: number, quantity: number) => {
    setItems((prev) =>
      prev
        .map((item) =>
          item.productId === productId
            ? { ...item, quantity: Math.max(1, Math.min(quantity, 99)) }
            : item,
        )
        .filter((item) => item.quantity > 0),
    );
  }, []);

  const clear = useCallback(() => {
    setItems([]);
  }, []);

  const isInCart = useCallback(
    (productId: number) => items.some((item) => item.productId === productId),
    [items],
  );

  const totalItems = useMemo(
    () => items.reduce((sum, item) => sum + item.quantity, 0),
    [items],
  );

  const totalPrice = useMemo(
    () => items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      totalItems,
      totalPrice,
      addItem,
      removeItem,
      updateQuantity,
      clear,
      isInCart,
    }),
    [
      items,
      totalItems,
      totalPrice,
      addItem,
      removeItem,
      updateQuantity,
      clear,
      isInCart,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCart debe usarse dentro de <CartProvider>");
  }
  return context;
}